import { Location } from '@angular/common';
import { Component, model, OnDestroy, OnInit, output } from '@angular/core';

import { LexXiModalComponent } from 'app/components/xi-components/lex-xi-modal/lex-xi-modal.component';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { Document } from 'app/providers/storage-api/document.service';
import { StorageListResult } from 'app/providers/storage-api.service';
import { DocHighlightService } from 'app/services/doc-highlight.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { DocumentViewOptions } from '../document-view/document-view.component';

@EnableClassTelemetry()
@Component({
  selector: 'app-document-view-dialog',
  templateUrl: './document-view-dialog.component.html',
  styleUrls: ['./document-view-dialog.component.scss'],
  standalone: false,
})
export class DocumentViewDialogComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly options = model<DocumentViewOptions>();
  readonly modalClosed = output<boolean>();

  hasChanged: boolean;
  private currentHighlight: { entity: string; analyticItem: string } | null = null;

  constructor(
    private lexXiModal: LexXiModalComponent,
    private groupService: GroupService,
    private location: Location,
    private channelService: DocHighlightService
  ) {}

  ngOnInit(): void {
    this.lexXiModal.onClose = () => {
      this.lexXiModal.close.emit(this.hasChanged);
    };
  }

  onHighlightChange(highlight: { entity: string; analyticItem: string } | null): void {
    if (highlight === null) {
      return;
    }
    this.currentHighlight = highlight;
    this.channelService.send({
      type: 'dialog-highlight',
      documentId: this.options().documentId,
      ...highlight,
    });
  }

  onOpenNewTab() {
    function addQueryParams(link: string, name: string, val: string | string[]): string {
      if (val != null) {
        if (Array.isArray(val)) {
          return val.reduce((prev, curr) => addQueryParams(prev, name, curr), link);
        }

        return `${link}&${name}=${encodeURIComponent(val)}`;
      }
      return link;
    }

    const width = window.top.outerWidth * 0.9;
    const height = window.top.outerHeight * 0.9;
    const x = window.top.outerWidth / 2 + window.top.screenX - width / 2;
    const y = window.top.outerHeight / 2 + window.top.screenY - height / 2;
    const options = this.options();

    let url = this.location.prepareExternalUrl(
      `groups/${this.groupService.getSelectedGroupId(true)}` +
        `/projects/${options.projectId}` +
        `/dashboards/${options.dashboardId}` +
        `/collections/${options.collectionName}` +
        `/documents/${options.documentId}?`
    );

    const params = {
      entity: this.currentHighlight?.entity ?? options.entity,
      entityDisplayName: options.entityDisplayName,
      analyticItem: this.currentHighlight?.analyticItem ?? options.analyticItem,
      analyses: options.page.items
        .find((d) => d.id === options.documentId)
        .analyses.map((a) => a.analysisName),
      nlpFields: JSON.stringify(
        options.nlpFields?.filter((f) => f.display === false).map((f) => f.id)
      ),
      metadataFields: JSON.stringify(
        options.metadataFields?.filter((f) => f.display === false).map((f) => f.id)
      ),
      showNlpData: options.showNlpData,
      showMetadata: options.showMetadata,
      dataSourceType: options.widgetDataSource?.type,
      analysisGroupId: options.widgetDataSource?.groupId,
      isSocial: options.widgetDataSource?.isSocial,
    };

    for (const k of Object.keys(params)) {
      url = addQueryParams(url, k, params[k]);
    }

    this.channelService.open(options.documentId, (msg) => {
      if (msg.type === 'window-highlight') {
        this.options.update((o) => ({ ...o, entity: msg.entity, analyticItem: msg.analyticItem }));
      }
    });

    window.open(url, '_blank', `width=${width},height=${height},top=${y},left=${x},titlebar=false`);
  }

  ngOnDestroy(): void {
    this.channelService.close();
  }

  onDocumentIdChange(documentId: string): void {
    this.options.update((options) => {
      options.documentId = documentId;
      return options;
    });
  }

  onPageChange(page: StorageListResult<Document>) {
    this.options.update((options) => {
      options.page = page;
      return options;
    });
  }
}
